import React from 'react';
import { Link } from 'react-router-dom';
import '../styles/LegalPages.css';

const Privacy = () => {
  return (
    <div className="legal-page">
      <div className="legal-container">
        <div className="legal-header">
          <h1>Privacy Notice</h1>
          <p className="legal-subtitle">
            In compliance with the <strong>Data Privacy Act of 2012 (Republic Act No. 10173)</strong> and the
            regulations of the <strong>National Privacy Commission (NPC)</strong> of the Philippines.
          </p>
          <p className="legal-effective">Effective Date: April 15, 2026 &nbsp;|&nbsp; Last Updated: April 15, 2026</p>
        </div>

        <div className="legal-body">

          <section className="legal-section">
            <h2>1. Identity of the Personal Information Controller</h2>
            <p>
              This Privacy Notice is issued by <strong>Logica Technology</strong>, the operator of the
              E-Invitation digital service accessible at this website (collectively referred to as
              "we," "us," or "our"). Logica Technology acts as the <em>Personal Information Controller</em>
              as defined under RA 10173.
            </p>
            <p>For privacy concerns, you may reach us at:</p>
            <ul>
              <li>Email: <a href="mailto:dpo@logicatechnology.com">dpo@logicatechnology.com</a></li>
              <li>Website: <a href="https://logicatechnology.com" target="_blank" rel="noopener noreferrer">logicatechnology.com</a></li>
              <li>Facebook: <a href="https://www.facebook.com/profile.php?id=61585468153192" target="_blank" rel="noopener noreferrer">Logica Technology Facebook Page</a></li>
            </ul>
          </section>

          <section className="legal-section">
            <h2>2. What Personal Information We Collect</h2>
            <p>We collect the following personal information when you use our services:</p>
            <h3>a. Booking Form</h3>
            <ul>
              <li>Full name</li>
              <li>Email address</li>
              <li>Phone / mobile number</li>
              <li>Event details (event type, date, time, venue)</li>
              <li>Design preferences and additional notes</li>
            </ul>
            <h3>b. RSVP Responses</h3>
            <ul>
              <li>Name of the RSVP respondent</li>
              <li>Attendance status and number of guests</li>
              <li>Optional message</li>
            </ul>
            <h3>c. Automatically Collected Information</h3>
            <ul>
              <li>Browser type and device information (non-identifiable, for technical purposes only)</li>
            </ul>
          </section>

          <section className="legal-section">
            <h2>3. Purpose of Data Collection</h2>
            <p>
              Your personal information is collected and processed for the following legitimate purposes,
              consistent with Section 11 of RA 10173:
            </p>
            <ul>
              <li>To process and fulfill your e-invitation booking request</li>
              <li>To communicate with you regarding your order (via Messenger, SMS, or email)</li>
              <li>To send payment instructions and confirm transactions</li>
              <li>To deliver the final e-invitation file</li>
              <li>To handle RSVP responses on behalf of our clients</li>
              <li>To respond to inquiries and provide customer support</li>
              <li>To comply with legal obligations</li>
            </ul>
            <p>
              We do <strong>not</strong> use your personal information for automated decision-making,
              profiling, or direct marketing without your explicit consent.
            </p>
          </section>

          <section className="legal-section">
            <h2>4. Legal Basis for Processing</h2>
            <p>We process your personal information under the following lawful bases under RA 10173:</p>
            <ul>
              <li><strong>Consent</strong> — By submitting the booking or RSVP form, you consent to the processing of your personal information for the stated purposes.</li>
              <li><strong>Contractual necessity</strong> — Processing is necessary to fulfill the service you requested (e.g., creating and delivering your e-invitation).</li>
              <li><strong>Legitimate interest</strong> — To operate, improve, and communicate about our services.</li>
            </ul>
          </section>

          <section className="legal-section">
            <h2>5. How We Store and Protect Your Data</h2>
            <p>
              We implement reasonable and appropriate organizational, technical, and physical security
              measures to protect your personal information against unauthorized access, disclosure,
              alteration, or destruction, as required by Section 20 of RA 10173 and NPC Circular 16-03.
            </p>
            <ul>
              <li>Booking data is transmitted over secure (HTTPS) connections</li>
              <li>Access to collected data is limited to authorized personnel only</li>
              <li>We do not store payment information — payments are handled directly via Messenger</li>
            </ul>
          </section>

          <section className="legal-section">
            <h2>6. Retention Period</h2>
            <p>
              We retain your personal information only for as long as necessary to fulfill the purposes
              stated in this Privacy Notice, or as required by applicable law. Booking and transaction
              records are retained for a period of <strong>one (1) year</strong> from the date of
              service completion, after which they are securely deleted or anonymized.
            </p>
          </section>

          <section className="legal-section">
            <h2>7. Sharing of Personal Information</h2>
            <p>
              We do <strong>not</strong> sell, trade, or share your personal information with third
              parties for commercial purposes. We may share your information only in the following
              limited circumstances:
            </p>
            <ul>
              <li>With service providers directly involved in delivering your e-invitation (e.g., email delivery services)</li>
              <li>When required by law, regulation, or a valid order from a government authority or court</li>
              <li>With your explicit consent</li>
            </ul>
          </section>

          <section className="legal-section">
            <h2>8. Your Rights as a Data Subject</h2>
            <p>
              Under the Data Privacy Act of 2012 (RA 10173) and NPC issuances, you have the following
              rights regarding your personal information:
            </p>
            <div className="rights-grid">
              <div className="right-item">
                <h4>Right to be Informed</h4>
                <p>You have the right to know how your data is being collected, processed, and used.</p>
              </div>
              <div className="right-item">
                <h4>Right to Access</h4>
                <p>You may request a copy of the personal information we hold about you.</p>
              </div>
              <div className="right-item">
                <h4>Right to Correction</h4>
                <p>You may request correction of inaccurate or incomplete personal information.</p>
              </div>
              <div className="right-item">
                <h4>Right to Erasure / Blocking</h4>
                <p>You may request the deletion or blocking of your personal data under lawful grounds.</p>
              </div>
              <div className="right-item">
                <h4>Right to Object</h4>
                <p>You may object to the processing of your personal information.</p>
              </div>
              <div className="right-item">
                <h4>Right to Data Portability</h4>
                <p>You may request your personal data in a structured, commonly used format.</p>
              </div>
              <div className="right-item">
                <h4>Right to Lodge a Complaint</h4>
                <p>You may file a complaint with the National Privacy Commission (NPC) at <a href="https://www.privacy.gov.ph" target="_blank" rel="noopener noreferrer">privacy.gov.ph</a>.</p>
              </div>
              <div className="right-item">
                <h4>Right to Damages</h4>
                <p>You are entitled to claim compensation for damages suffered due to inaccurate, incomplete, or unauthorized use of your personal information.</p>
              </div>
            </div>
            <p>
              To exercise any of these rights, please contact us at{' '}
              <a href="mailto:dpo@logicatechnology.com">dpo@logicatechnology.com</a>.
              We will respond within <strong>fifteen (15) working days</strong> from receipt of your request.
            </p>
          </section>

          <section className="legal-section">
            <h2>9. Cookies and Similar Technologies</h2>
            <p>
              This website does not use tracking cookies or third-party analytics tools that collect
              personal information. Technical session data may be temporarily held in your browser's
              local storage solely for operational functionality (e.g., form state).
            </p>
          </section>

          <section className="legal-section">
            <h2>10. Changes to This Privacy Notice</h2>
            <p>
              We may update this Privacy Notice from time to time to reflect changes in our practices
              or legal requirements. The updated version will be posted on this page with a revised
              effective date. We encourage you to review this notice periodically.
            </p>
          </section>

          <section className="legal-section">
            <h2>11. Contact Us / Data Protection Officer</h2>
            <p>
              For any privacy-related concerns, requests, or complaints, please contact us:
            </p>
            <div className="legal-contact-box">
              <p><strong>Logica Technology — Data Protection Officer</strong></p>
              <p>Email: <a href="mailto:dpo@logicatechnology.com">dpo@logicatechnology.com</a></p>
              <p>Website: <a href="https://logicatechnology.com" target="_blank" rel="noopener noreferrer">logicatechnology.com</a></p>
              <p>
                If your concern is not resolved, you may file a complaint with the{' '}
                <strong>National Privacy Commission (NPC)</strong>:{' '}
                <a href="https://www.privacy.gov.ph" target="_blank" rel="noopener noreferrer">www.privacy.gov.ph</a>
              </p>
            </div>
          </section>

        </div>

        <div className="legal-footer">
          <Link to="/" className="legal-back-link">← Back to Home</Link>
          <Link to="/terms" className="legal-back-link">View Terms &amp; Conditions →</Link>
        </div>
      </div>
    </div>
  );
};

export default Privacy;
